/**
 * Why a folder listing failed, as the browser must SAY it (STD-029 B-5).
 *
 * A deep link can be wrong in three ways that need three different sentences,
 * and the classification is by `error_code`, never by message text — the same
 * rule `uploadResults` states, for the same reason: a message is a string a
 * translator may change, an `error_code` is the contract.
 *
 * **404 is one sentence for two situations, deliberately.** The endpoint answers
 * 404 for a row that does not exist, a row the caller may not resolve, and a
 * tombstone — because a 403 would confirm the folder is real. Splitting them
 * here would rebuild that oracle in the client's copy.
 *
 * Pure, so it is unit-testable in the node-environment suite.
 */
import { extractErrorCode } from '../../utils/errorHandler';
import { isAxiosErrorWithResponse } from '../../types/errors';

export type FolderFailure =
  /** Absent, tombstoned, or not visible to this caller. One message for all three. */
  | 'not-found'
  /** The link names a FILE. The caller can demonstrably see it, so we say so. */
  | 'not-a-folder'
  /** Anything else — a registry outage, a network drop, a bare validation 422. */
  | 'unavailable';

/** The HTTP status of an axios failure, or `undefined` if it never got a reply. */
export function errorStatus(error: unknown): number | undefined {
  return isAxiosErrorWithResponse(error) ? error.response?.status : undefined;
}

/**
 * Is this a 4xx, i.e. a refusal the server will repeat identically?
 *
 * Used to switch the listing query's retry off for exactly those. A dead deep
 * link would otherwise spin through the global `retry: 1` before settling, and
 * the user reads that delay as the app trying to find a folder that is gone.
 */
export function isClientError(error: unknown): boolean {
  const status = errorStatus(error);
  return status !== undefined && status >= 400 && status < 500;
}

export function folderLoadFailure(error: unknown): FolderFailure {
  const status = errorStatus(error);
  if (status === 404) return 'not-found';
  // A bare 422 is FastAPI's own validation refusal (a `limit` out of range, a
  // path param that is not a uuid). It says nothing about the node, so it must
  // NOT borrow the "that link points at a file" sentence.
  if (status === 422 && extractErrorCode(error) === 'FILE_OBJECT_NOT_FOLDER') return 'not-a-folder';
  return 'unavailable';
}
